/**
 * InterconnectionSection Component
 *
 * Grid interconnection settings: service capacity, standard vs flexible mode,
 * UL 3141 parameters, and uptime tier selection.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  InputAdornment,
  Paper,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const UPTIME_TIERS = [
  { value: 'tier_1', label: 'Tier I - Basic', sla: '99.671' },
  { value: 'tier_2', label: 'Tier II - Redundant Components', sla: '99.741' },
  { value: 'tier_3', label: 'Tier III - Concurrently Maintainable', sla: '99.982' },
  { value: 'tier_4', label: 'Tier IV - Fault Tolerant', sla: '99.995' },
];

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: vortxColors.white,
    '& fieldset': { borderColor: vortxColors.teal },
    '&:hover fieldset': { borderColor: vortxColors.gold },
    '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': {
    color: vortxColors.stone,
    '&.Mui-focused': { color: vortxColors.gold },
  },
  '& .MuiFormHelperText-root': {
    color: vortxColors.stone,
  },
  '& .MuiInputAdornment-root': {
    color: vortxColors.gold,
  },
};

function InterconnectionSection({ project, onUpdate }) {
  const [formData, setFormData] = useState({
    service_capacity_kw: '',
    interconnection_mode: 'standard',
    curtailment_hours: '',
    curtailed_capacity_kw: '',
    peak_trigger_kw: '',
    uptime_tier: 'tier_3',
    sla_pct: '99.982',
  });

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        service_capacity_kw: project.service_capacity_kw || '',
        interconnection_mode: project.interconnection_mode || 'standard',
        curtailment_hours: project.curtailment_hours || '',
        curtailed_capacity_kw: project.curtailed_capacity_kw || '',
        peak_trigger_kw: project.peak_trigger_kw || '',
        uptime_tier: project.uptime_tier || 'tier_3',
        sla_pct: project.sla_pct || '99.982',
      });
    }
  }, [project]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setSaveSuccess(false);
  };

  const handleModeChange = (_, newMode) => {
    if (newMode === null) return;
    setFormData((prev) => ({
      ...prev,
      interconnection_mode: newMode,
    }));
    setSaveSuccess(false);
  };

  const handleTierChange = (event) => {
    const tier = event.target.value;
    const tierData = UPTIME_TIERS.find((t) => t.value === tier);
    setFormData((prev) => ({
      ...prev,
      uptime_tier: tier,
      sla_pct: tierData ? tierData.sla : prev.sla_pct,
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save interconnection config:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: vortxTypography.heading,
          color: vortxColors.white,
          mb: 3,
        }}
      >
        Grid Interconnection
      </Typography>

      <Grid container spacing={3}>
        {/* Service Capacity */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Service Capacity"
            type="number"
            value={formData.service_capacity_kw}
            onChange={handleChange('service_capacity_kw')}
            InputProps={{
              endAdornment: <InputAdornment position="end">kW</InputAdornment>,
            }}
            helperText="Maximum grid import capacity at point of interconnection"
            sx={textFieldSx}
          />
        </Grid>

        {/* Mode Toggle */}
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ color: vortxColors.stone, mb: 1 }}>
            Interconnection Mode
          </Typography>
          <ToggleButtonGroup
            value={formData.interconnection_mode}
            exclusive
            onChange={handleModeChange}
            sx={{
              '& .MuiToggleButton-root': {
                color: vortxColors.stone,
                borderColor: vortxColors.teal,
                px: 3,
                '&.Mui-selected': {
                  color: vortxColors.gold,
                  bgcolor: 'rgba(178, 140, 80, 0.15)',
                  borderColor: vortxColors.gold,
                  '&:hover': {
                    bgcolor: 'rgba(178, 140, 80, 0.25)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(178, 140, 80, 0.1)',
                },
              },
            }}
          >
            <ToggleButton value="standard">Standard</ToggleButton>
            <ToggleButton value="flexible">Flexible</ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {/* Flexible mode fields */}
        {formData.interconnection_mode === 'flexible' && (
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                bgcolor: 'rgba(178, 140, 80, 0.05)',
                border: `1px solid ${vortxColors.gold}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: vortxColors.gold, mb: 2 }}>
                Flexible Interconnection Parameters
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Curtailment Hours"
                    type="number"
                    value={formData.curtailment_hours}
                    onChange={handleChange('curtailment_hours')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">hrs/yr</InputAdornment>,
                    }}
                    helperText="Max hours grid can curtail per year"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Curtailed Capacity"
                    type="number"
                    value={formData.curtailed_capacity_kw}
                    onChange={handleChange('curtailed_capacity_kw')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                    }}
                    helperText="Reduced capacity during curtailment events"
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Peak Trigger"
                    type="number"
                    value={formData.peak_trigger_kw}
                    onChange={handleChange('peak_trigger_kw')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                    }}
                    helperText="Grid load threshold that triggers curtailment"
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Uptime Tier */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Uptime Tier"
            value={formData.uptime_tier}
            onChange={handleTierChange}
            helperText="Uptime Institute tier classification"
            sx={textFieldSx}
          >
            {UPTIME_TIERS.map((tier) => (
              <MenuItem key={tier.value} value={tier.value}>{tier.label}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="SLA Target"
            type="number"
            value={formData.sla_pct}
            onChange={handleChange('sla_pct')}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Service level agreement uptime target"
            sx={textFieldSx}
          />
        </Grid>

        {/* Save */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
                },
              }}
            >
              Save Changes
            </Button>
            {saveSuccess && <Alert severity="success">Changes saved successfully!</Alert>}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default InterconnectionSection;
